'use client';

import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

type AuthMode = 'login' | 'signup';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupForm {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

const mockCredentials = [
  { role: 'Creator', email: 'priya@biolink.app', password: 'Creator@2026' },
  { role: 'Business', email: 'rohanbiz@biolink.app', password: 'Business@2026' },
];

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const router = useRouter();

  const loginForm = useForm<LoginForm>({
    defaultValues: { email: '', password: '', remember: false },
  });

  const signupForm = useForm<SignupForm>({
    defaultValues: { username: '', email: '', password: '', confirmPassword: '', terms: false },
  });

  const handleLoginSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/login with { email, password }
    await new Promise((r) => setTimeout(r, 1200));

    const valid = mockCredentials.some(
      (c) => c.email === data.email && c.password === data.password
    );

    if (!valid) {
      loginForm.setError('email', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      setIsLoading(false);
      return;
    }

    toast.success('Welcome back! Redirecting to dashboard…');
    await new Promise((r) => setTimeout(r, 800));
    router.push('/dashboard');
    setIsLoading(false);
  };

  const handleSignupSubmit = async (data: SignupForm) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/signup with { username, email, password }
    await new Promise((r) => setTimeout(r, 1400));
    toast.success('Account created! Setting up your profile…');
    await new Promise((r) => setTimeout(r, 600));
    router.push('/create-profile');
    setIsLoading(false);
  };

  const handleUsernameChange = async (value: string) => {
    if (value.length < 3) { setUsernameAvailable(null); return; }
    // BACKEND INTEGRATION: GET /api/check-username?username=value
    await new Promise((r) => setTimeout(r, 400));
    setUsernameAvailable(!['priya', 'rohan', 'admin', 'biolink'].includes(value.toLowerCase()));
  };

  const autofillCredential = (cred: typeof mockCredentials[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-brand flex-col justify-between p-12 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10" />
        <div className="absolute bottom-20 -left-10 w-48 h-48 rounded-full bg-white/10" />
        <div className="absolute top-1/2 right-10 w-32 h-32 rounded-full bg-white/5" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <AppLogo size={28} />
            </div>
            <span className="font-bold text-2xl text-white tracking-tight">BioLink</span>
          </div>

          <h2 className="text-4xl font-800 text-white leading-tight mb-6">
            Your entire online presence,<br />one beautiful link.
          </h2>
          <p className="text-white/70 text-lg leading-relaxed max-w-sm">
            Join 2.4M+ creators who use BioLink to share everything with their audience.
          </p>
        </div>

        {/* Feature list */}
        <div className="relative z-10 space-y-4">
          {[
            { icon: '🔗', text: 'All your links in one place' },
            { icon: '📊', text: 'Real-time analytics & insights' },
            { icon: '🎨', text: '10+ stunning templates' },
            { icon: '🚀', text: 'Live in under 5 minutes' },
          ].map((item, i) => (
            <div key={`brand-feat-${i}`} className="flex items-center gap-3">
              <span className="text-xl">{item.icon}</span>
              <span className="text-white/80 text-sm font-500">{item.text}</span>
            </div>
          ))}
        </div>

        {/* Social proof */}
        <div className="relative z-10 bg-white/10 rounded-2xl p-4 border border-white/20">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex -space-x-2">
              {['AK', 'RM', 'SG', 'NK'].map((init, i) => (
                <div
                  key={`avatar-${i}`}
                  className="w-8 h-8 rounded-full bg-white/30 border-2 border-white/50 flex items-center justify-center text-white text-xs font-700"
                >
                  {init}
                </div>
              ))}
            </div>
            <p className="text-white text-sm font-600">2.4M+ creators</p>
          </div>
          <p className="text-white/70 text-xs">Trusted by creators, influencers & businesses worldwide</p>
        </div>
      </div>

      {/* Right: Form Panel */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 bg-background overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-bold text-xl text-foreground">BioLink</span>
          </div>

          {/* Mode toggle */}
          <div className="flex bg-muted rounded-xl p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all duration-200 ${
                mode === 'login' ? 'bg-card text-foreground card-shadow' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign in
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2.5 rounded-lg text-sm font-600 transition-all duration-200 ${
                mode === 'signup' ? 'bg-card text-foreground card-shadow' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create account
            </button>
          </div>

          {/* Google auth */}
          <button className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-border bg-card hover:bg-muted transition-colors mb-6 text-sm font-600 text-foreground">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs text-muted-foreground font-500">or continue with email</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* LOGIN FORM */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLoginSubmit)} className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-600 text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  className={`w-full px-4 py-3 rounded-xl border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                    loginForm.formState.errors.email ? 'border-danger' : 'border-border'
                  }`}
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
                  })}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-danger mt-1.5">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="login-password" className="block text-sm font-600 text-foreground mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Your password"
                    className={`w-full px-4 py-3 pr-12 rounded-xl border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                      loginForm.formState.errors.password ? 'border-danger' : 'border-border'
                    }`}
                    {...loginForm.register('password', { required: 'Password is required', minLength: { value: 6, message: 'Minimum 6 characters' } })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-danger mt-1.5">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded accent-primary"
                    {...loginForm.register('remember')}
                  />
                  <span className="text-sm text-muted-foreground">Remember me</span>
                </label>
                <a href="#" className="text-sm text-primary font-600 hover:underline">Forgot password?</a>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary py-3.5 rounded-xl text-sm font-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in…
                  </>
                ) : (
                  'Sign in to BioLink'
                )}
              </button>
            </form>
          )}

          {/* SIGNUP FORM */}
          {mode === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)} className="space-y-4">
              <div>
                <label htmlFor="signup-username" className="block text-sm font-600 text-foreground mb-1.5">
                  Username
                  <span className="text-xs text-muted-foreground font-400 ml-2">— your public URL</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-500">
                    biolink.app/
                  </span>
                  <input
                    id="signup-username"
                    type="text"
                    placeholder="yourname"
                    className={`w-full pl-28 pr-10 py-3 rounded-xl border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                      signupForm.formState.errors.username ? 'border-danger' : 'border-border'
                    }`}
                    {...signupForm.register('username', {
                      required: 'Username is required',
                      minLength: { value: 3, message: 'Minimum 3 characters' },
                      pattern: { value: /^[a-z0-9._]+$/, message: 'Only lowercase letters, numbers, dots and underscores' },
                      onChange: (e) => handleUsernameChange(e.target.value),
                    })}
                  />
                  {usernameAvailable !== null && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2">
                      {usernameAvailable ? (
                        <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                    </span>
                  )}
                </div>
                {usernameAvailable === false && (
                  <p className="text-xs text-danger mt-1.5">This username is already taken</p>
                )}
                {usernameAvailable === true && (
                  <p className="text-xs text-success mt-1.5">Username is available!</p>
                )}
                {signupForm.formState.errors.username && (
                  <p className="text-xs text-danger mt-1.5">{signupForm.formState.errors.username.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-email" className="block text-sm font-600 text-foreground mb-1.5">
                  Email address
                </label>
                <input
                  id="signup-email"
                  type="email"
                  placeholder="you@example.com"
                  className={`w-full px-4 py-3 rounded-xl border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                    signupForm.formState.errors.email ? 'border-danger' : 'border-border'
                  }`}
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
                  })}
                />
                {signupForm.formState.errors.email && (
                  <p className="text-xs text-danger mt-1.5">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-sm font-600 text-foreground mb-1.5">
                  Password
                </label>
                <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with at least one number</p>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    className={`w-full px-4 py-3 pr-12 rounded-xl border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                      signupForm.formState.errors.password ? 'border-danger' : 'border-border'
                    }`}
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-xs text-danger mt-1.5">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-confirm" className="block text-sm font-600 text-foreground mb-1.5">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="signup-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Repeat your password"
                    className={`w-full px-4 py-3 pr-12 rounded-xl border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all ${
                      signupForm.formState.errors.confirmPassword ? 'border-danger' : 'border-border'
                    }`}
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-danger mt-1.5">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded accent-primary mt-0.5 flex-shrink-0"
                  {...signupForm.register('terms', { required: 'You must accept the terms' })}
                />
                <span className="text-sm text-muted-foreground">
                  I agree to the{' '}
                  <a href="#" className="text-primary hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="#" className="text-primary hover:underline">Privacy Policy</a>
                </span>
              </label>
              {signupForm.formState.errors.terms && (
                <p className="text-xs text-danger">{signupForm.formState.errors.terms.message}</p>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary py-3.5 rounded-xl text-sm font-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Creating your account…
                  </>
                ) : (
                  'Create Free Account'
                )}
              </button>
            </form>
          )}

          {/* Demo credentials box */}
          {mode === 'login' && (
            <div className="mt-6 rounded-xl border border-info/20 bg-info/5 p-4">
              <p className="text-xs font-700 text-info mb-3 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Demo accounts — click to autofill
              </p>
              <div className="space-y-2">
                {mockCredentials.map((cred) => (
                  <button
                    key={`cred-${cred.role}`}
                    type="button"
                    onClick={() => autofillCredential(cred)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-card border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
                  >
                    <div>
                      <span className="text-xs font-700 text-foreground">{cred.role}</span>
                      <p className="text-xs text-muted-foreground font-mono">{cred.email}</p>
                    </div>
                    <span className="text-xs text-primary opacity-0 group-hover:opacity-100 transition-opacity font-600">
                      Use →
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}